/**
 * Auth0 OAuth error codes that mean the cached session can no longer mint an
 * access token silently and the user must log in interactively again.
 *
 * `getAccessTokenSilently()` restores a session from the localStorage cache on
 * reload, so a stale or expired refresh token is not discovered until the token
 * call runs — at which point auth0-spa-js throws a `GenericError` (or one of its
 * subclasses, e.g. `MissingRefreshTokenError`) carrying one of these codes in
 * its string `error` field. Anything outside this set (a network blip, a
 * timeout) is treated as transient and must NOT force a redirect, so it cannot
 * trap the user in a login loop.
 */
const REAUTH_REQUIRED_ERROR_CODES = new Set([
  'consent_required',
  'interaction_required',
  'invalid_grant',
  'login_required',
  'missing_refresh_token',
])

/**
 * Whether an error thrown by `getAccessTokenSilently()` (or surfaced by a query)
 * means the session is dead and interactive re-authentication is required.
 *
 * auth0-spa-js errors expose the OAuth error code on a string `error` property;
 * this narrows the unknown caught value to that shape and matches it against the
 * known re-auth codes. Returns `false` for anything unrecognized so transient
 * failures never trigger a redirect.
 */
export const isReauthRequiredError = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null || !('error' in error)) {
    return false
  }
  const errorCode = (error as { error: unknown }).error
  return (
    typeof errorCode === 'string' && REAUTH_REQUIRED_ERROR_CODES.has(errorCode)
  )
}
